/**
 * Shape tools: rect, ellipse, line, arrow.
 *
 * All four are "click-drag-release" tools that produce a single element.
 * We share one class and switch on `kind` because the interaction is
 * identical — the only difference is which SceneElement type we create
 * and whether we store a geometry bbox or a points list.
 *
 * Shift during the drag:
 *   - rect/ellipse: constrain to square/circle
 *   - line/arrow:   snap to nearest cardinal axis
 */

import type { SceneElement, ElementType } from "../../lib/scene";
import type { SceneStore } from "../store/sceneStore";
import { drawElement } from "../render/drawElement";
import { bboxFromCorners, type Tool, type ToolPointerEvent } from "./tool";

type ShapeKind = "rect" | "ellipse" | "line" | "arrow";

export class ShapeTool implements Tool {
  readonly kind: ShapeKind;

  private active = false;
  private startX = 0;
  private startY = 0;
  private endX = 0;
  private endY = 0;
  private shift = false;

  constructor(kind: ShapeKind) {
    this.kind = kind;
  }

  isActive(): boolean {
    return this.active;
  }

  onPointerDown(evt: ToolPointerEvent): void {
    if (evt.button !== 0) return;
    this.active = true;
    this.startX = evt.x;
    this.startY = evt.y;
    this.endX = evt.x;
    this.endY = evt.y;
    this.shift = evt.shift;
  }

  onPointerMove(evt: ToolPointerEvent): void {
    if (!this.active) return;
    this.endX = evt.x;
    this.endY = evt.y;
    this.shift = evt.shift;
  }

  onPointerUp(_evt: ToolPointerEvent, store: SceneStore): void {
    if (!this.active) return;
    this.active = false;

    const el = this.buildElement(store);
    if (!el) return;
    // Zero-area shapes (a single click with no drag) are dropped — they'd
    // be invisible and just clutter undo history.
    if (el.width < 1 && el.height < 1) return;
    store.upsertLocal(el);
  }

  cancel(): void {
    this.active = false;
  }

  drawPreview(ctx: CanvasRenderingContext2D, store: SceneStore): void {
    if (!this.active) return;
    const el = this.buildElement(store);
    if (el) drawElement(ctx, el);
  }

  // -----------------------------------------------------------------------

  private buildElement(store: SceneStore): SceneElement | null {
    let ex = this.endX;
    let ey = this.endY;
    if (this.shift) {
      // Constrain rect/ellipse to a square; line/arrow to the nearest axis.
      if (this.kind === "rect" || this.kind === "ellipse") {
        const dx = ex - this.startX;
        const dy = ey - this.startY;
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        ex = this.startX + Math.sign(dx || 1) * d;
        ey = this.startY + Math.sign(dy || 1) * d;
      } else {
        const dx = ex - this.startX;
        const dy = ey - this.startY;
        if (Math.abs(dx) >= Math.abs(dy)) ey = this.startY;
        else ex = this.startX;
      }
    }

    if (this.kind === "rect" || this.kind === "ellipse") {
      const b = bboxFromCorners(this.startX, this.startY, ex, ey);
      return store.createElement(this.kind, {
        x: b.x,
        y: b.y,
        width: b.w,
        height: b.h,
      });
    }

    // line / arrow: store two points relative to bbox min
    const minX = Math.min(this.startX, ex);
    const minY = Math.min(this.startY, ey);
    const type: ElementType = this.kind;
    return store.createElement(type, {
      x: minX,
      y: minY,
      width: Math.abs(ex - this.startX),
      height: Math.abs(ey - this.startY),
      points: [
        this.startX - minX,
        this.startY - minY,
        ex - minX,
        ey - minY,
      ],
    });
  }
}
